import { Component, inject, input, output, computed, DestroyRef, signal } from '@angular/core';
import { NgClass, DatePipe } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { filter, switchMap, catchError, of, forkJoin, merge, map, skip } from 'rxjs';
import { MenuItem, DetailPageInfo, Assignment } from '../../models';
import {
  SearchService,
  AuthService,
  SidebarService,
  DispatchActionsConfigService,
  AssignmentService,
  PartiesService,
} from '../../services';
import { CreateDispatchUiService } from '../../services/create-dispatch-ui.service';
import { ClaimImportService } from '../../services/claim-import.service';
import {
  getMissingFieldSpecs,
  isClaimDataMissing,
  isPolicyDataMissing,
  isLossDataMissing,
  isUnitsDataMissing,
  isPartiesDataMissing,
} from '../../utils';
import { environment } from '../../../environments/environment';
import { DispatchActionsDrawerComponent } from '../dispatch-actions-drawer/dispatch-actions-drawer.component';
import { DispatchActionsConfig } from '../../models/dispatch-actions-config.model';

interface DetailMenuItem {
  label: string;
  icon: string;
  route: string;
  level: number;
  hasSubmenu?: boolean;
  expanded?: boolean;
  parent?: string;
  hidden?: boolean;
  external?: boolean;
  isLink?: boolean;
}

/** Detail menu labels mapped to their route segments for active-state detection. */
const ROUTE_BY_LABEL: Record<string, string> = {
  'Claim Overview': '/claim-overview',
  Claim: '/claim-overview',
  Loss: '/loss',
  Policy: '/policy',
  Parties: '/parties',
  Units: '/units',
  Notes: '/notes',
  'Stop Storage Letters': '/stop-storage-letters',
  'Activity Log': '/activity-log',
};

/** All detail route segments (used to detect the Assignment fallback state). */
const ALL_DETAIL_ROUTES = Object.values(ROUTE_BY_LABEL);

/** Wizard tab order: each tab unlocks once every prior tab's mandatory fields are resolved. */
const SEQUENTIAL_TABS = ['Claim', 'Policy', 'Loss', 'Units', 'Parties'];

/** Links that stay locked until the appraiser is assigned. */
const POST_ASSIGNMENT_LINKS = [
  'Notes',
  'Payments',
  'Stop Storage Letters',
  'Body Shop Portal',
  'Salvage',
  'Activity Log',
];

@Component({
  selector: 'app-sidebar',
  imports: [NgClass, RouterModule, DatePipe, DispatchActionsDrawerComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly dispatchConfigService = inject(DispatchActionsConfigService);
  private readonly sidebarService = inject(SidebarService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly createDispatchUiService = inject(CreateDispatchUiService);
  private readonly claimImportService = inject(ClaimImportService);
  private readonly assignmentService = inject(AssignmentService);
  private readonly partiesService = inject(PartiesService);

  /** claimId last eager-flagged as incomplete; prevents re-flagging tabs on the same claim. */
  private eagerFlaggedClaimId: number | null = null;

  // Inputs / outputs
  detailMode = input<boolean>(false);
  detailPageInfo = input<DetailPageInfo | null>(null);
  assignment = input<Assignment | null>(null);
  printAssignmentSheet = output<void>();

  // Reactive state
  currentRoute = signal(this.router.url);
  version = signal('');
  buildTimestamp = signal('');
  isCollapsed = signal(false);
  isMobileSidebarVisible = computed(() => this.sidebarService.isSidebarVisible());
  dispatchActionsVisible = signal(false);

  /** True when this claim has a pending manual dispatch (appraiser not yet selected). */
  readonly isPendingManualDispatch = computed(() => {
    const pending = this.createDispatchUiService.pendingManualDispatch();
    const info = this.detailPageInfo();
    if (!pending || !info) return false;
    if (pending.claimId === 0) return pending.claimNum === info.claimNumber;
    return pending.claimId === Number(info.claimSequence);
  });

  /** Sidebar tab labels that have unresolved mandatory field issues for the current claim. */
  readonly missingTabs = computed(() => this.createDispatchUiService.pendingManualMissingTabs());

  /**
   * Whether the displayed claim meets legacy's "Get Appraisers" claim-level requirement
   * (companyCd + lobCd both present) — gates the "Assignment" menu item. Taken when the claim
   * loads; null until then, in which case isMenuItemDisabled falls back to the completeness check.
   */
  private readonly assignmentReady = signal<boolean | null>(null);

  /** Composite key identifying the claim currently shown in detail mode. */
  private readonly currentClaimKey = computed(() => {
    const info = this.detailPageInfo();
    return info ? `${info.claimNumber}-${info.claimSequence}` : '';
  });
  dispatchActionsConfig = signal<DispatchActionsConfig>({
    appointmentId: '',
    claimNumber: '',
    claimSequence: '',
    timestamps: {},
    presets: {},
  });

  menuItems: MenuItem[] = [
    { label: 'Assignments', icon: 'pi pi-clipboard', route: '/assignments', active: true },
    {
      label: 'CheckWriter',
      icon: 'pi pi-external-link',
      route: '',
      active: false,
      external: true,
    },
    { label: 'LegacyMap', icon: 'pi pi-external-link', route: '', active: false, external: true },
  ];

  detailMenu: DetailMenuItem[] = [
    {
      label: 'Claim Overview',
      icon: 'pi pi-file',
      route: '/claim-overview',
      level: 1,
      hasSubmenu: true,
      expanded: false,
    },
    {
      label: 'Claim',
      icon: 'pi pi-circle-fill',
      route: '/claim-overview',
      level: 2,
      parent: 'Claim Overview',
      hidden: true,
    },
    {
      label: 'Policy',
      icon: 'pi pi-circle-fill',
      route: '/policy',
      level: 2,
      parent: 'Claim Overview',
      hidden: true,
    },
    {
      label: 'Loss',
      icon: 'pi pi-circle-fill',
      route: '/loss',
      level: 2,
      parent: 'Claim Overview',
      hidden: true,
    },
    {
      label: 'Units',
      icon: 'pi pi-circle-fill',
      route: '/units',
      level: 2,
      parent: 'Claim Overview',
      hidden: true,
    },
    {
      label: 'Parties',
      icon: 'pi pi-circle-fill',
      route: '/parties',
      level: 2,
      parent: 'Claim Overview',
      hidden: true,
    },
    { label: 'Assignment', icon: 'pi pi-clipboard', route: '', level: 1 },
    { label: 'Notes', icon: 'pi pi-comment', route: '/notes', level: 1 },
    { label: 'Payments', icon: 'pi pi-external-link', route: '', level: 1, external: true },
    {
      label: 'Stop Storage Letters',
      icon: 'pi pi-envelope',
      route: '/stop-storage-letters',
      level: 1,
    },
    { label: 'Body Shop Portal', icon: 'pi pi-external-link', route: '', level: 1, isLink: true },
    { label: 'ImageRight', icon: 'pi pi-external-link', route: '', level: 1, isLink: true },
    { label: 'Salvage', icon: 'pi pi-external-link', route: '', level: 1, isLink: true },
    { label: 'Rental Assignment', icon: 'pi pi-external-link', route: '', level: 1, isLink: true },
    { label: 'Activity Log', icon: 'pi pi-table', route: '/activity-log', level: 1 },
  ];

  constructor() {
    // Load version and build timestamp with cache-busting.
    this.http
      .get(`/assets/version.txt?_=${Date.now()}`, { responseType: 'text' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const lines = data.split('\n');
          this.version.set('V ' + (lines[0]?.replace('Version:', '').trim() || '0.0.0'));
          const ts = lines[1]?.replace('Build Timestamp:', '').trim();
          if (ts) this.buildTimestamp.set(ts);
        },
        error: (err) => console.error('Failed to load version.txt', err),
      });

    // Sync Claim Overview submenu expanded state on every navigation.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e: NavigationEnd) => {
        this.currentRoute.set(e.url);
        const childRoutes = this.detailMenu
          .filter((i) => i.parent === 'Claim Overview')
          .map((i) => i.route);
        const parent = this.detailMenu.find((i) => i.label === 'Claim Overview');
        this.setSubmenuExpanded(
          childRoutes.some((r) => e.url.includes(r)),
          parent,
        );
      });

    // Open dispatch actions when triggered by another component (e.g. calendar dialog).
    this.sidebarService.dispatchActionsTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.openDispatchActions());

    // Recompute existence + missing-tab state when the displayed claim CHANGES (reset the
    // snapshot first) or after any save on the SAME claim (live refresh, no reset → no flicker),
    // so newly created records unlock their dependent tabs immediately. Runs for manual dispatches
    // too, so the wizard uses the same stable record-existence gate (not the order-dependent
    // field-completeness fallback, which flip-flops as incomplete tabs are visited).
    merge(
      toObservable(this.currentClaimKey).pipe(map(() => true)),
      toObservable(this.createDispatchUiService.claimDataVersion).pipe(
        skip(1),
        map(() => false),
      ),
    )
      .pipe(
        switchMap((isClaimChange) => {
          this.createDispatchUiService.clearAllMissingTabs();
          if (isClaimChange) this.assignmentReady.set(null);
          const claimId = this.detailPageInfo()?.claimSequence;
          if (!claimId) return of(null);
          return forkJoin({
            claim: this.assignmentService.getClaimUnitsWithVehicle(claimId),
            parties: this.partiesService.getAddresses(claimId).pipe(catchError(() => of([]))),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        const claimEntry = result?.claim?.claims?.[0];
        if (!claimEntry) return;
        const missing: Array<[string, boolean]> = [
          [
            'Claim',
            isClaimDataMissing(claimEntry.claim, claimEntry as unknown as Record<string, unknown>),
          ],
          ['Policy', isPolicyDataMissing(claimEntry.policy)],
          [
            'Loss',
            isLossDataMissing(claimEntry.claim, claimEntry as unknown as Record<string, unknown>),
          ],
          ['Units', isUnitsDataMissing(claimEntry.units)],
          ['Parties', isPartiesDataMissing(result?.parties)],
        ];
        for (const [tab, isMissing] of missing) {
          if (isMissing) this.createDispatchUiService.reportMissingTab(tab);
          else this.createDispatchUiService.clearMissingTab(tab);
        }

        // Legacy "Get Appraisers" claim-level requirement: companyCd + lobCd both present.
        const companyCd = String(
          claimEntry.companyCd ?? (claimEntry.claim?.['companyCd'] as string) ?? '',
        ).trim();
        const lobCd = String(
          (claimEntry.policy?.['lobCd'] as string) ?? (claimEntry.policy?.['lob'] as string) ?? '',
        ).trim();
        this.assignmentReady.set(!!companyCd && !!lobCd);
      });

    // On a pending manual dispatch, check whether the first unit has missing mandatory
    // fields and update the indicator. switchMap cancels any in-flight check on change.
    toObservable(this.createDispatchUiService.pendingManualDispatch)
      .pipe(
        switchMap((pending) => {
          if (!pending) {
            this.eagerFlaggedClaimId = null;
            this.createDispatchUiService.clearAllMissingTabs();
            return of(null);
          }
          // Assume every wizard tab is incomplete until its component proves otherwise.
          // Done once per claim so a save-triggered re-emit doesn't stomp cleared flags.
          if (this.eagerFlaggedClaimId !== pending.claimId) {
            this.eagerFlaggedClaimId = pending.claimId;
            this.createDispatchUiService.reportMissingTab('Claim');
            this.createDispatchUiService.reportMissingTab('Policy');
            this.createDispatchUiService.reportMissingTab('Loss');
            this.createDispatchUiService.reportMissingTab('Units');
            this.createDispatchUiService.reportMissingTab('Parties');
          }

          // Blank manual dispatch (no template): no units exist yet — skip the fetch.
          if (!pending.templateUsed) {
            return of(null);
          }
          return this.claimImportService.fetchNewDispatchClaim(pending.claimId).pipe(
            switchMap((claim) => {
              const units = (claim.unitassignments ?? []) as Array<{ unitId?: number }>;
              const firstUnitId = units[0]?.unitId;
              if (!firstUnitId) {
                // No reserves yet; the dispatch checkpoint validates before appraiser selection.
                return of(null);
              }
              return this.claimImportService
                .getDispatchableUnitDetails(pending.claimId, firstUnitId)
                .pipe(catchError(() => of(null)));
            }),
            catchError(() => of(null)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (!result) return;
        const { unitVehicle, unitProperty, unitSubType } = result;
        const missing = getMissingFieldSpecs(unitVehicle, unitProperty, unitSubType ?? '');
        if (missing && missing.length > 0) {
          this.createDispatchUiService.reportMissingTab('Units');
        } else {
          this.createDispatchUiService.clearMissingTab('Units');
        }
      });
  }

  // ─── Active state ───────────────────────────────────────────────

  /**
   * 'black' when the item's tab has missing fields and is currently active, 'yellow' when
   * inactive, null otherwise. Drives the sidebar warning-triangle icons.
   */
  getMissingFieldsIcon(item: DetailMenuItem): 'black' | 'yellow' | null {
    if (!this.missingTabs().includes(item.label)) return null;
    return this.isDetailMenuActive(item) ? 'black' : 'yellow';
  }

  /**
   * True when the item is muted and unclickable. Claim → Policy → Loss → Units → Parties
   * unlock in sequence as each prior tab's mandatory fields resolve, and Assignment unlocks
   * once all do. Post-assignment links stay locked only during a pending manual dispatch.
   */
  isMenuItemDisabled(item: DetailMenuItem): boolean {
    // Legacy never disables navigation or the Get-Appraisers action — you can always click;
    // a missing prerequisite surfaces a message instead (per-page add-guards, and the
    // Assignment readiness check in handleDetailMenuClick). Incomplete records only show a
    // warning icon (getMissingFieldsIcon).
    if (SEQUENTIAL_TABS.includes(item.label) || item.label === 'Assignment') return false;

    // Post-assignment links stay locked until the appraiser is assigned.
    return this.isPendingManualDispatch() && POST_ASSIGNMENT_LINKS.includes(item.label);
  }

  menuItemTitle(item: DetailMenuItem): string {
    if (!this.isMenuItemDisabled(item)) return item.label;
    return 'Complete assignment before accessing this section';
  }

  isDetailMenuActive(item: DetailMenuItem): boolean {
    // Parent items with submenus never show active highlight.
    if (item.hasSubmenu) return false;
    // While the reserve-selection drawer is open, only "Assignment" is active.
    if (this.createDispatchUiService.reserveDrawerOpen()) return item.label === 'Assignment';
    const route = this.currentRoute();
    const segment = ROUTE_BY_LABEL[item.label];
    if (segment) return route.includes(segment);
    // "Assignment" is active when no other detail route matches.
    if (item.label === 'Assignment') {
      return this.detailMode() && ALL_DETAIL_ROUTES.every((r) => !route.includes(r));
    }
    return false;
  }

  // ─── Sidebar chrome ─────────────────────────────────────────────

  toggleSidebar(): void {
    this.isCollapsed.update((v) => !v);
  }

  closeMobileSidebar(): void {
    this.sidebarService.hideSidebar();
  }

  // ─── Main menu click ────────────────────────────────────────────

  handleMenuClick(item: MenuItem): void {
    if (item.label === 'LegacyMap') {
      this.openLegacyMap();
      return;
    }

    if (item.label === 'CheckWriter') {
      this.openCheckWriter();
      return;
    }

    if (item.external && item.route) {
      window.open(item.route, '_blank');
    } else if (item.label === 'Assignments') {
      this.searchService.clearFilter();
      this.router
        .navigateByUrl('/', { skipLocationChange: true })
        .then(() => this.router.navigate(['/assignments']));
    }
  }

  private openLegacyMap(): void {
    const profile = this.authService.getStoredUserProfile();
    const userId = profile?.userId;
    const userName = profile?.fullName ?? profile?.userName;

    if (!userId || !userName) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Cannot open LegacyMap: User details are not available.',
      });
      return;
    }

    const mapPath = `map/${encodeURIComponent(String(userId))}/${encodeURIComponent(userName)}`;
    window.open(`${environment.LEGACY_MAP_BASE_URL}${mapPath}`, '_blank');
  }

  private openCheckWriter(): void {
    window.open(`${environment.LEGACY_MAP_BASE_URL}appointments`, '_blank');
  }

  // ─── Detail menu click ──────────────────────────────────────────

  handleDetailMenuClick(item: DetailMenuItem): void {
    if (this.isMenuItemDisabled(item)) return;

    if (item.label === 'Payments') {
      this.openPayments();
      return;
    }
    if (item.label === 'Salvage') {
      this.openSalvageAssignment();
      return;
    }
    if (item.label === 'Rental Assignment') {
      this.openRentalAssignment();
      return;
    }
    if (item.label === 'Body Shop Portal') {
      this.openBodyShopPortal();
      return;
    }
    if (item.label === 'ImageRight') {
      this.openImageRight();
      return;
    }
    if (item.isLink && item.route && item.route !== '#') {
      window.open(item.route, '_blank');
      return;
    }

    const info = this.detailPageInfo();
    if (!info?.claimNumber) return;

    if (item.label === 'Assignment') {
      // Legacy "Get Appraisers" claim-level requirement: companyCd + lobCd present. Rather than
      // disabling the link, surface a meaningful message so the user knows what to complete.
      if (this.assignmentReady() === false) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Not ready to assign',
          detail: 'Enter Company and LOB before assigning an appraiser.',
        });
        return;
      }

      const pending = this.createDispatchUiService.pendingManualDispatch();
      if (pending && this.isPendingManualDispatch()) {
        if (pending.claimId === 0) {
          this.createDispatchRecordThenAssign(pending.claimNum);
          return;
        }

        this.createDispatchUiService.assignExistingClaim.set({
          claimId: pending.claimId,
          claimNum: pending.claimNum,
        });
        return;
      }

      // No live wizard session (e.g. claim reopened via search after a refresh). Gating
      // above guarantees the tabs are complete, so only the appraiser state is left to check.
      this.routeToAssignmentOrReserveSelection(info.claimNumber, info.claimSequence);
      this.setSubmenuExpanded(false);
      return;
    }

    if (item.label === 'Stop Storage Letters') {
      this.router.navigate(['/stop-storage-letters', this.getCompositeId()]);
      this.setSubmenuExpanded(false);
      return;
    }

    // Claim Overview & its children use the same claim route params.
    const routeParams = [item.route, info.claimNumber, info.claimSequence];

    // Preselect the assigned unit in Units — otherwise it defaults to unit 0.
    const unitId = item.route === '/units' ? this.assignment()?.id2 : undefined;
    this.router.navigate(routeParams, unitId ? { queryParams: { unitId } } : undefined);

    if (item.hasSubmenu) {
      this.setSubmenuExpanded(!item.expanded, item);
    } else if (!item.parent) {
      this.setSubmenuExpanded(false);
    }
  }

  // ─── Submenu toggle (single source of truth) ───────────────────

  private setSubmenuExpanded(expanded: boolean, toggleItem?: DetailMenuItem): void {
    const parent = this.detailMenu.find((i) => i.label === 'Claim Overview');
    if (!parent) return;
    if (toggleItem) parent.expanded = expanded;
    else if (!expanded) parent.expanded = false;
    this.detailMenu.forEach((i) => {
      if (i.parent === 'Claim Overview') i.hidden = !parent.expanded;
    });
  }

  toggleSubmenu(item: DetailMenuItem): void {
    this.setSubmenuExpanded(!item.expanded, item);
  }

  // ─── External app launchers ─────────────────────────────────────

  openPayments(): void {
    const claimNumber = this.detailPageInfo()?.claimNumber;

    if (!claimNumber) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Cannot open Payments: Claim number not available.',
      });
      return;
    }

    // id6 is the dispId the check-writer uses (?dispId=) to prepopulate the Payment Element.
    const dispatchId = this.detailPageInfo()?.dispatchId;
    const path = dispatchId
      ? `checkWriter/${encodeURIComponent(claimNumber)}/${encodeURIComponent(dispatchId)}`
      : `checkWriter/${encodeURIComponent(claimNumber)}`;

    window.open(`${environment.LEGACY_MAP_BASE_URL}${path}`, '_blank');
  }

  openSalvageAssignment(): void {
    const claimNumber = this.detailPageInfo()?.claimNumber;
    if (!claimNumber) return;
    const userId = this.authService.getCurrentUserId();
    const userIdLower = userId ? String(userId).toLowerCase() : '';
    const encodedUserId = encodeURIComponent(userIdLower);

    window.open(`${environment.SALVAGE_ASSIGNMENT_URL}${claimNumber}/${encodedUserId}`, '_blank');
  }

  openRentalAssignment(): void {
    const claimNumber = this.detailPageInfo()?.claimNumber;
    if (!claimNumber) return;
    const userId = this.authService.getCurrentUserId();
    const userIdLower = userId ? String(userId).toLowerCase() : '';
    const encodedUserId = encodeURIComponent(userIdLower);

    window.open(`${environment.RENTAL_ASSIGNMENT_URL}${claimNumber}/${encodedUserId}`, '_blank');
  }

  /** Opens the ImageRight file explorer for the current claim in a new tab. */
  openImageRight(): void {
    const claimNumber = this.detailPageInfo()?.claimNumber;
    if (!claimNumber) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Cannot open ImageRight: Claim number not available.',
      });
      return;
    }

    window.open(`${environment.IMAGE_RIGHT_URL}${encodeURIComponent(claimNumber)}`, '_blank');
  }

  /** Opens the Body Shop Portal (CBSP) in a new tab using the login-cached profile. */
  openBodyShopPortal(): void {
    const claimNumber = this.detailPageInfo()?.claimNumber;
    const vin = this.detailPageInfo()?.vehicle?.vin;

    if (!claimNumber || !vin) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Cannot open Body Shop Portal: Vehicle VIN or claim number not available.',
      });
      return;
    }

    const profile = this.authService.getStoredUserProfile();
    const userId = profile?.streamAssociations?.[0]?.streamId ?? null;

    if (!userId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Authentication Error',
        detail:
          'Cannot open Body Shop Portal: Unable to retrieve user profile. Please try logging out and back in.',
      });
      return;
    }

    // Build handshake URL (matches legacy).
    const timestamp = new Date().getTime();
    const urlParams = `${timestamp}/${userId}/${claimNumber}/${vin}`;
    const encodedParams = btoa(urlParams);
    const url = `${environment.CBSP_BASE_URL}handshake/${encodedParams}`;

    window.open(url, '_blank');
  }

  // ─── Print ──────────────────────────────────────────────────────

  handlePrintClick(): void {
    this.printAssignmentSheet.emit();
  }

  // ─── Dispatch actions ───────────────────────────────────────────

  openDispatchActions(): void {
    const info = this.detailPageInfo();
    const currentAssignment = this.assignment();
    if (!info?.appointmentId || !info?.userId || !currentAssignment) {
      this.dispatchActionsVisible.set(true);
      return;
    }

    // Build initial config from the assignment (same source as assignments-table).
    this.dispatchActionsConfig.set(
      this.dispatchConfigService.buildConfig(currentAssignment, info.appointmentId),
    );

    // Load presets, contact phone, and unit name in parallel, then open the dialog.
    this.dispatchConfigService
      .loadPresetsAndPhone(
        info.appointmentId,
        info.userId,
        currentAssignment.seq,
        currentAssignment.unit,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.dispatchActionsConfig.update((c) => ({
            ...c,
            timestamps: result.timestamps,
            presets: result.presets,
            contactPhone: result.contactPhone,
            stopAllCommInd: result.stopAllCommInd,
            unitName: result.unitName ?? c.unitName,
          }));
          this.dispatchActionsVisible.set(true);
        },
        error: () => {
          // Open the dialog with available data even if the preset fetch fails.
          this.dispatchActionsVisible.set(true);
        },
      });
  }

  // ─── Helpers ────────────────────────────────────────────────────

  private createDispatchRecordThenAssign(claimNum: string): void {
    const userId = this.authService.getCurrentUserId();
    this.claimImportService
      .createNewDispatchRecord(claimNum, 'nj', userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (claimId) => {
          this.createDispatchUiService.pendingManualDispatch.set({
            claimId,
            claimNum,
            templateUsed: true,
            partialManual: false,
          });
          this.createDispatchUiService.assignExistingClaim.set({ claimId, claimNum });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Dispatch Error',
            detail: 'Failed to create dispatch record. Please verify claim number and try again.',
          });
        },
      });
  }

  private getCompositeId(): string {
    const info = this.detailPageInfo();
    return `${info?.claimNumber}-${info?.claimSequence}-${info?.vendorSequence}`;
  }

  /**
   * "Assignment" clicked outside a live wizard: view the existing appointment if one
   * exists, otherwise jump straight into reserve/appraiser selection for the claim.
   */
  private routeToAssignmentOrReserveSelection(claimNumber: string, claimSeq: string): void {
    this.assignmentService
      .getAssignmentsByClaimNumberViaGatewayPost(claimNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((assignments) => {
        const hasRealAssignment = assignments.some(
          (a) =>
            (a.seq === claimSeq || a.claimSequence === claimSeq || a.id1 === claimSeq) &&
            !!(a.appraiser || a.appraiserName) &&
            !!(a.apptId || a.appointmentID),
        );
        const claimId = Number(claimSeq);
        if (!hasRealAssignment && claimId) {
          this.createDispatchUiService.assignExistingClaim.set({ claimId, claimNum: claimNumber });
          return;
        }
        this.router.navigate(['/assignments/detail', this.getCompositeId()]);
      });
  }
}
